import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface SiteHeaderProps {
  overlay?: boolean;
}

const navItems = [
  { label: "首页", href: "/" },
  { label: "电影", href: "/category/movie" },
  { label: "电视剧", href: "/category/tv" },
  { label: "关于我们", href: "/about" },
  { label: "联系我们", href: "/contact" },
];

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setMenuOpen(false);
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  const solid = scrolled || !overlay;

  return (
    <header
      className={`site-header fixed top-0 left-0 right-0 z-50 ${
        solid
          ? "bg-background-100/95 backdrop-blur border-b border-background-200/70"
          : "bg-gradient-to-b from-background-50/80 to-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20 gap-4">
          <a href="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center text-xl">
              <i className="ri-movie-2-fill" aria-hidden="true" />
            </span>
            <span className="font-heading text-lg md:text-xl font-bold tracking-wide text-foreground-950">
              天堂电影网
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="px-3.5 py-2 text-sm text-foreground-300 hover:text-accent-400 rounded-md hover:bg-background-200/60 transition-colors whitespace-nowrap"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="hidden md:flex items-center" role="search">
              <div className="flex items-center bg-background-50/70 border border-background-300/60 rounded-md pl-3 pr-1 h-10 focus-within:border-primary-400 transition-colors">
                <i className="ri-search-line text-base text-foreground-400" aria-hidden="true" />
                <input
                  type="text"
                  name="q"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影片、剧集"
                  aria-label="搜索影视内容"
                  className="w-36 lg:w-44 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 px-2 outline-none"
                />
                <button
                  type="submit"
                  className="h-8 px-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  搜索
                </button>
              </div>
            </form>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开菜单"
              aria-expanded={menuOpen}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-300/60 text-foreground-900 cursor-pointer"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-100/98 backdrop-blur border-t border-background-200/70 animate-fade-in">
          <div className="px-4 py-4 space-y-4">
            <form onSubmit={handleSearch} role="search">
              <div className="flex items-center bg-background-50/70 border border-background-300/60 rounded-md pl-3 pr-1 h-11">
                <i className="ri-search-line text-base text-foreground-400" aria-hidden="true" />
                <input
                  type="text"
                  name="q"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影片、剧集"
                  aria-label="搜索影视内容"
                  className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 px-2 outline-none"
                />
                <button
                  type="submit"
                  className="h-9 px-3 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer"
                >
                  搜索
                </button>
              </div>
            </form>
            <nav className="flex flex-col" aria-label="移动端导航">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-between py-3 border-b border-background-200/60 text-foreground-800"
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true" />
                </a>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}